'use client';

import React from 'react';
import BaseLink, { BaseLinkProps } from './BaseLink';
import { Sys, InternalPage } from 'libs/contentful/types';
import { ButtonCtaLevel } from 'components/shared/Buttons/BaseButton';
import { useAnalyticsModuleTracking } from 'components/analytics/AnalyticsModuleTracking';
import { useLeadGenerationInteraction } from 'components/analytics/hooks/useLeadGenerationInteraction';
import { convertObjectToArray } from 'components/analytics/utils';
import { LEAD_GENERATION_INTERACTION_TYPES, LeadGenerationInteractionType } from 'components/analytics/types';
import {
  trackInternalLinkTapped,
  trackOutboundLinkTapped,
  trackEngagementLink,
  trackEnrollmentCtaTapped,
  trackModuleElementTapped,
  trackSecondaryCtaClicked,
} from 'components/analytics/eventHandlerTracking';
import { useLoginLinkHandler } from 'components/analytics/hooks/useLoginLinkHandler';
import {
  getInteractionType,
  getCTAType,
  getSecondaryCTAIndex,
  getLinkType,
  isLoginLink,
  constructHref,
  resolveAnonIdToken,
} from 'utils/linkHelpers';
import { usePage } from 'contexts/PageProvider';

// Export BaseLink for components that need links without analytics
export { default as BaseLink } from './BaseLink';
export type { BaseLinkProps } from './BaseLink';

export interface LinkProps extends Omit<BaseLinkProps, 'href'> {
  href?: string;
  internalPage?: InternalPage;
  __typename?: string;
  sys?: Sys;
  withCtaAnalytics?: boolean;
  isSubnav?: boolean;
  ctaLevel?: ButtonCtaLevel;
  className?: string;
}

/**
 * Enhanced Link component that combines URL building with analytics
 * - URLs are complete from server-side transformation
 * - Analytics are added directly in this component
 *
 * For links without analytics, use BaseLink component instead
 */
export default function Link({
  withCtaAnalytics = false,
  isSubnav = false,
  ctaLevel,
  onClick,
  href,
  internalPage,
  children,
  className,
  ...baseLinkProps
}: LinkProps) {
  const { enrollmentUrlContext } = usePage();

  // Determine the final href using the shared utility function
  const finalHref = constructHref(href, internalPage, enrollmentUrlContext);

  // Analytics setup
  const isLogin = isLoginLink(finalHref);
  const { attributes } = useAnalyticsModuleTracking();
  const includeCtaAnalytics = withCtaAnalytics || baseLinkProps.asButton || false;
  const interactionType = getInteractionType(finalHref, includeCtaAnalytics, isSubnav, ctaLevel ?? 'primary');
  const location = interactionType === LEAD_GENERATION_INTERACTION_TYPES.MODULE_ELEMENT_TAPPED ? 'LINK' : finalHref;
  const ctaType = getCTAType(interactionType, includeCtaAnalytics);

  // Extract text content from children
  // Narrows an unparsed value to a primitive before using it.
  // oxlint-disable-next-line anti-slop/no-runtime-typeof
  const linkText = typeof children === 'string' ? children : '';
  const actionDetail = children?.toString() ?? '';

  const handleLoginClick = useLoginLinkHandler({ cta_copy: linkText });

  const analyticsProps = {
    interaction_type: interactionType as LeadGenerationInteractionType,
    interaction_details: {
      location: location,
      action_detail: actionDetail,
    },
    button_location: attributes.moduleType,
    ...(interactionType === LEAD_GENERATION_INTERACTION_TYPES.ENROLLMENT_CTA_TAPPED && {
      button_location: attributes.moduleType,
      email_entered: false,
      ignored_qsps: {},
      user_alias: {
        alias_label: 'pre_enrollment',
        alias_name: {},
      },
    }),
    ...(interactionType === LEAD_GENERATION_INTERACTION_TYPES.SECONDARY_CTA_CLICKED && {
      cta_index: '0',
    }),
  };

  const trackLeadGenerationInteraction = useLeadGenerationInteraction(analyticsProps);

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    // Handle fragment navigation
    // Detects the browser at runtime; there is no value to parse at a boundary here.
    // oxlint-disable-next-line anti-slop/no-runtime-typeof
    if (finalHref.startsWith('#') && typeof window !== 'undefined' && window.location.hash === finalHref) {
      window.dispatchEvent(new Event('hashchange'));
    }

    // Track login links separately from other interactions
    if (isLogin) {
      handleLoginClick(e);
      return;
    }

    const decoratedHref = resolveAnonIdToken(finalHref);
    if (decoratedHref !== finalHref) {
      e.currentTarget.href = decoratedHref;
    }

    // Track analytics based on interaction type for non-login links
    const commonAttributes = {
      label: linkText,
      item_value: decoratedHref,
      module: {
        module_title: attributes.moduleTitle,
        module_name: attributes.moduleName,
        module_index: attributes.moduleIndex,
        module_type: attributes.moduleType,
      },
    };

    switch (interactionType) {
      case LEAD_GENERATION_INTERACTION_TYPES.INTERNAL_LINK_TAPPED:
        trackInternalLinkTapped({
          ...commonAttributes,
          link_type: getLinkType(finalHref),
        });
        break;
      case LEAD_GENERATION_INTERACTION_TYPES.OUTBOUND_LINK_TAPPED:
        trackOutboundLinkTapped({
          ...commonAttributes,
          link_type: getLinkType(finalHref),
        });
        break;
      case LEAD_GENERATION_INTERACTION_TYPES.ENGAGEMENT_LINK_TAPPED:
        trackEngagementLink({
          ...commonAttributes,
          cta_type: ctaType,
        });
        break;
      case LEAD_GENERATION_INTERACTION_TYPES.ENROLLMENT_CTA_TAPPED:
        trackEnrollmentCtaTapped({
          ...commonAttributes,
          cta_type: ctaType,
          button_location: attributes.moduleType,
        });
        break;
      case LEAD_GENERATION_INTERACTION_TYPES.MODULE_ELEMENT_TAPPED:
        trackModuleElementTapped(commonAttributes);
        break;
      case LEAD_GENERATION_INTERACTION_TYPES.SECONDARY_CTA_CLICKED:
        const ctaIndex = String(getSecondaryCTAIndex(e.currentTarget));
        trackSecondaryCtaClicked({
          ...commonAttributes,
          cta_index: ctaIndex,
        });
        break;
    }

    // Track lead generation interaction with callback; href-derived fields are
    // built here, not at render, so they carry the resolved [anon_id] value
    const interactionProps = {
      ...(interactionType !== LEAD_GENERATION_INTERACTION_TYPES.MODULE_ELEMENT_TAPPED && {
        interaction_details: {
          location: decoratedHref,
          action_detail: actionDetail,
        },
        additional_data: convertObjectToArray({
          destination: decoratedHref,
          label: linkText,
          button_name: linkText,
          cta_type: ctaType,
          link_type: getLinkType(finalHref),
        }),
      }),
      ...(interactionType === LEAD_GENERATION_INTERACTION_TYPES.SECONDARY_CTA_CLICKED && {
        cta_index: String(getSecondaryCTAIndex(e.currentTarget)),
      }),
    };

    trackLeadGenerationInteraction(interactionProps, () => onClick?.(e));
  };

  // Render BaseLink with analytics onClick handler
  // Pass the already-calculated href to avoid duplicate calculation
  return (
    <BaseLink href={finalHref} onClick={handleClick} className={className} {...baseLinkProps}>
      {children}
    </BaseLink>
  );
}
